import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Always send the auth cookie on cross-site requests.
axios.defaults.withCredentials = true;

// Process the OAuth `session_id` returned in the URL fragment by Emergent
// Auth. Backend exchanges it for user data and sets the httpOnly cookie.
export async function exchangeSessionId(sessionId) {
  const res = await axios.post(`${API}/auth/session`, { session_id: sessionId });
  return res.data;
}

export async function fetchMe() {
  try {
    const res = await axios.get(`${API}/auth/me`, { timeout: 8000 });
    return res.data || null;
  } catch {
    return null;
  }
}

export async function logout() {
  try {
    await axios.post(`${API}/auth/logout`);
  } catch {
    // ignore
  }
}

// Send the user to Emergent's Google OAuth. The user lands back at our
// origin with `#session_id=...` which AuthCallback exchanges.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH (must always read window.location.origin live).
//
// `returnPath` lets callers route back to a specific page (e.g. /admin) after
// auth. Defaults to root.
export function startGoogleLogin(returnPath = "/") {
  const path = typeof returnPath === "string" && returnPath.startsWith("/") ? returnPath : "/";
  const redirect = `${window.location.origin}${path}`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
    redirect
  )}`;
}

// Billing
export async function fetchPlans() {
  const res = await axios.get(`${API}/billing/plans`);
  return res.data || [];
}

export async function startCheckout(planId) {
  const res = await axios.post(`${API}/billing/checkout`, {
    plan_id: planId,
    origin_url: window.location.origin,
  });
  return res.data;
}

export async function pollCheckoutStatus(sessionId) {
  const res = await axios.get(`${API}/billing/status/${sessionId}`);
  return res.data;
}

// Daily VIP crate
export async function claimDailyCrate() {
  const res = await axios.post(`${API}/vip/crate/claim`);
  return res.data;
}

export async function consumeReroll() {
  const res = await axios.post(`${API}/vip/reroll/consume`);
  return res.data;
}

export async function consumeLuckyRun() {
  const res = await axios.post(`${API}/vip/lucky-run/consume`);
  return res.data;
}

export async function fetchCosmeticsCatalog() {
  try {
    const res = await axios.get(`${API}/cosmetics/catalog`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function equipCosmetic(slot, cosmeticId) {
  const res = await axios.post(`${API}/cosmetics/equip`, {
    slot,
    cosmetic_id: cosmeticId,
  });
  return res.data;
}

// ---------- Phase 4: Account progression / Talents ----------
export async function fetchTalentsCatalog() {
  try {
    const res = await axios.get(`${API}/account/talents/catalog`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function spendTalent(talentId) {
  const res = await axios.post(`${API}/account/talents/spend`, {
    talent_id: talentId,
  });
  return res.data;
}

// ---------- Phase 5: Achievements ----------
export async function fetchAchievementsCatalog() {
  try {
    const res = await axios.get(`${API}/account/achievements/catalog`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function claimAchievement(achievementId) {
  const res = await axios.post(`${API}/account/achievements/claim`, {
    achievement_id: achievementId,
  });
  return res.data;
}

// ---------- Cosmetic Shop (Feb 2026) ----------
export async function purchaseCosmetic(cosmeticId) {
  const res = await axios.post(`${API}/cosmetics/purchase`, {
    cosmetic_id: cosmeticId,
  });
  return res.data;
}

export async function submitRunSummary({ wave, zombies, bossesDefeated, completed, playSeconds, coinsEarned }) {
  try {
    const res = await axios.post(`${API}/account/run-summary`, {
      wave: Math.max(1, parseInt(wave || 1, 10)),
      zombies: Math.max(0, parseInt(zombies || 0, 10)),
      bosses_defeated: Math.max(0, parseInt(bossesDefeated || 0, 10)),
      completed: !!completed,
      play_seconds: Math.max(0, parseInt(playSeconds || 0, 10)),
      coins_earned: Math.max(0, parseInt(coinsEarned || 0, 10)),
    });
    return res.data;
  } catch {
    return null;
  }
}
